/**
 * SINGLE SOURCE OF TRUTH for provider service categories.
 *
 * A provider's `category` is stored verbatim as one of these names (chosen during
 * onboarding), and Browse filters by regex-matching that stored value. So every
 * category link/chip in the app MUST use these exact names — anything else silently
 * returns zero providers.
 *
 * Several categories can be combined into one filter with "|" (e.g. the landing
 * page's event types), because the backend treats the value as a regex.
 */
import {
  Buildings, ForkKnife, CookingPot, Truck, Cake, MusicNotes, Headphones, Guitar,
  Microphone, PersonSimpleThrow, MicrophoneStage, MaskHappy, Camera, VideoCamera,
  ImageSquare, FlowerLotus, Palette, Flower, Armchair, Lightbulb, Users, Martini,
  ShieldCheck, House, Broom, Lightning, Wrench, Toolbox, Plant, Package, Sparkle, Scissors,
} from '@phosphor-icons/react';

export const CATEGORY_GROUPS = [
  { group: 'Venues', emoji: '🏛️', Icon: Buildings, cats: [
    { name: 'Venues', emoji: '🏛️', Icon: Buildings },
  ]},
  { group: 'Catering', emoji: '🍽️', Icon: ForkKnife, cats: [
    { name: 'Caterer', emoji: '🍽️', Icon: ForkKnife },
    { name: 'Private chef', emoji: '👨‍🍳', Icon: CookingPot },
    { name: 'Food truck', emoji: '🚚', Icon: Truck },
    { name: 'Cake designer', emoji: '🎂', Icon: Cake },
  ]},
  { group: 'Entertainment', emoji: '🎧', Icon: MusicNotes, cats: [
    { name: 'DJ', emoji: '🎧', Icon: Headphones },
    { name: 'Live band', emoji: '🎸', Icon: Guitar },
    { name: 'Live musicians', emoji: '🎻', Icon: MusicNotes },
    { name: 'Performer', emoji: '🎤', Icon: Microphone },
    { name: 'Dancers', emoji: '💃', Icon: PersonSimpleThrow },
    { name: 'MC / Host', emoji: '🎙️', Icon: MicrophoneStage },
    { name: 'Kids entertainment', emoji: '🤹', Icon: MaskHappy },
  ]},
  { group: 'Photo & Video', emoji: '📸', Icon: Camera, cats: [
    { name: 'Photographer', emoji: '📸', Icon: Camera },
    { name: 'Videographer', emoji: '🎥', Icon: VideoCamera },
    { name: 'Photobooth', emoji: '🖼️', Icon: ImageSquare },
  ]},
  { group: 'Decor & Rentals', emoji: '🎨', Icon: FlowerLotus, cats: [
    { name: 'Event decorator', emoji: '🎨', Icon: Palette },
    { name: 'Florist', emoji: '💐', Icon: Flower },
    { name: 'Rentals', emoji: '🪑', Icon: Armchair },
    { name: 'Lighting & AV', emoji: '💡', Icon: Lightbulb },
  ]},
  { group: 'Staffing', emoji: '👤', Icon: Users, cats: [
    { name: 'Bartender', emoji: '🍹', Icon: Martini },
    { name: 'Servers', emoji: '🍽️', Icon: ForkKnife },
    { name: 'Security', emoji: '🛡️', Icon: ShieldCheck },
  ]},
  { group: 'Home Services', emoji: '🏠', Icon: House, cats: [
    { name: 'Cleaner', emoji: '🧹', Icon: Broom },
    { name: 'Electrician', emoji: '⚡', Icon: Lightning },
    { name: 'Plumber', emoji: '🔧', Icon: Wrench },
    { name: 'Handyman', emoji: '🛠️', Icon: Toolbox },
    { name: 'Gardener', emoji: '🌿', Icon: Plant },
    { name: 'Mover', emoji: '📦', Icon: Package },
  ]},
  { group: 'Personal', emoji: '✨', Icon: Sparkle, cats: [
    { name: 'Makeup artist', emoji: '💄', Icon: Sparkle },
    { name: 'Barber', emoji: '💇', Icon: Scissors },
  ]},
];

/** Flat list of every category as { name, emoji } — for pickers, search, tiles. */
export const ALL_CATEGORY_OPTIONS = CATEGORY_GROUPS.flatMap((g) => g.cats);

/** Flat list of every category name, in group order. */
export const ALL_CATEGORIES = ALL_CATEGORY_OPTIONS.map((c) => c.name);

/**
 * Which services each kind of event can actually use — powers the landing page's
 * "What are you celebrating?" cards. Values are joined with "|" into one filter.
 */
export const EVENT_TYPE_SERVICES = {
  weddings: ['Venues', 'Caterer', 'Private chef', 'Cake designer', 'Photographer', 'Videographer', 'Photobooth',
    'Florist', 'Event decorator', 'Lighting & AV', 'Rentals', 'DJ', 'Live band', 'Live musicians', 'MC / Host',
    'Dancers', 'Bartender', 'Servers', 'Security', 'Makeup artist', 'Cleaner'],
  birthdays: ['Venues', 'Caterer', 'Food truck', 'Cake designer', 'DJ', 'Performer', 'Dancers', 'Kids entertainment',
    'Photobooth', 'Photographer', 'Event decorator', 'Florist', 'Rentals', 'Bartender', 'Cleaner'],
  corporate: ['Venues', 'Caterer', 'Servers', 'Security', 'Photographer', 'Videographer', 'Lighting & AV',
    'MC / Host', 'Live musicians', 'Performer', 'Rentals', 'Event decorator', 'Bartender', 'Cleaner'],
  houseparties: ['DJ', 'Live musicians', 'Bartender', 'Caterer', 'Food truck', 'Photobooth', 'Photographer',
    'Event decorator', 'Rentals', 'Lighting & AV', 'Security', 'Cleaner'],
};

/** Build a Browse href for one or many categories. */
export const categoryHref = (cats) =>
  `/browse?category=${encodeURIComponent(Array.isArray(cats) ? cats.join('|') : cats)}`;
